import { supabase } from '@/integrations/supabase/client';
import { differenceInDays } from 'date-fns';

// ============================================================================
// Types
// ============================================================================

// LED verde = ativo, amarelo = lead/inativo, vermelho = pendente
export type StatusLedCliente = 'lead' | 'ativo' | 'inativo' | 'pendente';

export interface ClienteReal {
  id: string;
  codigo_cliente: string | null;
  nome: string | null;
  telefone: string;
  email: string | null;
  avatar_url: string | null;
  servico_interesse: string | null;
  endereco_rua: string | null;
  endereco_numero: string | null;
  endereco_complemento: string | null;
  endereco_cidade: string | null;
  endereco_codigo_postal: string | null;
  endereco_pais: string | null;
  responsavel_id: string | null;
  responsavel_nome: string | null;
  arquivado: boolean;
  origem: string | null;
  criado_em: string | null;
  total_servicos: number;
  servicos_concluidos: number;
  valor_total_gasto: number;
  ultimo_servico: string | null;
  pendencias_pagamento: number;
  status_led: StatusLedCliente;
  is_lead: boolean;
}

export interface FiltrosClientesReais {
  busca?: string;
  status?: StatusLedCliente[];
  tipo?: ('lead' | 'cliente')[];
  origem?: string[];
  minServicos?: number;
  maxServicos?: number;
  minValor?: number;
  maxValor?: number;
  ultimoServicoApos?: Date;
  ultimoServicoAntes?: Date;
  primeiroContatoApos?: Date;
  primeiroContatoAntes?: Date;
  mostrarArquivados?: boolean;
}

export interface MetricasCliente {
  total_servicos: number;
  servicos_concluidos: number;
  valor_total_gasto: number;
  ultimo_servico: string | null;
  pendencias_pagamento: number;
}

export interface ClienteRow {
  id: string;
  codigo_cliente: string | null;
  nome: string | null;
  telefone: string;
  email: string | null;
  avatar_url: string | null;
  servico_interesse: string | null;
  endereco_rua: string | null;
  endereco_numero: string | null;
  endereco_complemento: string | null;
  endereco_cidade: string | null;
  endereco_codigo_postal: string | null;
  endereco_pais: string | null;
  responsavel_id: string | null;
  arquivado: boolean | null;
  origem: string | null;
  criado_em: string | null;
  responsavel: { nome: string | null } | { nome: string | null }[] | null;
}

export interface OrdemMetricsRow {
  id: string;
  cliente_id: string | null;
  status: string | null;
  valor_total: number | string | null;
  data_agendada: string | null;
  comprovativo_pagamento_url: string | null;
}

export interface OrdemServicoClienteRow {
  id: string;
  numero_os: string | null;
  status: string | null;
  valor_total: number | string | null;
  data_agendada: string | null;
  data_fim_execucao: string | null;
  comprovativo_pagamento_url: string | null;
  servicos: unknown;
  prestador: { id: string; profile: { nome: string | null } | { nome: string | null }[] | null } | null;
}

export interface OrdemServicoClienteComExtras extends OrdemServicoClienteRow {
  nota_avaliacao: number | null;
  prestador_nome: string | null;
}

// ============================================================================
// Fetchers — Supabase-touching, throw on error
// ============================================================================

export async function fetchClientesParaMetricas(): Promise<ClienteRow[]> {
  const { data, error } = await supabase
    .from('clientes_potenciais')
    .select(`
      id,
      codigo_cliente,
      nome,
      telefone,
      email,
      avatar_url,
      servico_interesse,
      endereco_rua,
      endereco_numero,
      endereco_complemento,
      endereco_cidade,
      endereco_codigo_postal,
      endereco_pais,
      responsavel_id,
      arquivado,
      origem,
      criado_em,
      responsavel:profiles!clientes_potenciais_responsavel_id_fkey(nome)
    `)
    .order('nome', { ascending: true });

  if (error) throw error;
  return (data ?? []) as ClienteRow[];
}

export async function fetchOrdensParaMetricas(): Promise<OrdemMetricsRow[]> {
  const { data, error } = await supabase
    .from('ordens_servico')
    .select('id, cliente_id, status, valor_total, data_agendada, comprovativo_pagamento_url');

  if (error) throw error;
  return (data ?? []) as OrdemMetricsRow[];
}

export async function fetchOrigensRaw(): Promise<{ origem: string | null }[]> {
  const { data, error } = await supabase
    .from('clientes_potenciais')
    .select('origem')
    .not('origem', 'is', null);

  if (error) throw error;
  return data ?? [];
}

export async function listOrdensServicoCliente(clienteId: string): Promise<OrdemServicoClienteComExtras[]> {
  const { data, error } = await supabase
    .from('ordens_servico')
    .select(`
      id,
      numero_os,
      status,
      valor_total,
      data_agendada,
      data_fim_execucao,
      comprovativo_pagamento_url,
      servicos,
      prestador:prestadores_servico(
        id,
        profile:profiles(nome)
      )
    `)
    .eq('cliente_id', clienteId)
    .order('data_agendada', { ascending: false });

  if (error) throw error;

  const rows = (data ?? []) as OrdemServicoClienteRow[];
  const osIds = rows.map((os) => os.id);

  const { data: avaliacoes } = await supabase
    .from('avaliacoes_servico')
    .select('ordem_servico_id, nota_geral')
    .in('ordem_servico_id', osIds);

  const avaliacoesMap = new Map(
    (avaliacoes ?? []).map((a) => [a.ordem_servico_id, a.nota_geral]),
  );

  return rows.map<OrdemServicoClienteComExtras>((os) => ({
    ...os,
    nota_avaliacao: avaliacoesMap.get(os.id) ?? null,
    prestador_nome:
      (os.prestador as { profile?: { nome?: string | null } | null } | null)?.profile?.nome || null,
  }));
}

// ============================================================================
// Aggregators — Pure functions, no I/O
// ============================================================================

export function calcularStatusLed(
  temServicos: boolean,
  ultimoServico: string | null,
  pendenciasPagamento: number,
): StatusLedCliente {
  if (!temServicos) return 'lead';
  if (pendenciasPagamento > 0) return 'pendente';
  if (!ultimoServico) return 'inativo';

  const diasUltimoServico = differenceInDays(new Date(), new Date(ultimoServico));
  if (diasUltimoServico <= 90) return 'ativo';
  return 'inativo';
}

export function agruparMetricasPorCliente(
  ordens: OrdemMetricsRow[],
): Map<string, MetricasCliente> {
  const metricsMap = new Map<string, MetricasCliente>();

  for (const os of ordens) {
    if (!os.cliente_id) continue;

    const current = metricsMap.get(os.cliente_id) ?? {
      total_servicos: 0,
      servicos_concluidos: 0,
      valor_total_gasto: 0,
      ultimo_servico: null,
      pendencias_pagamento: 0,
    };

    current.total_servicos += 1;

    if (os.status === 'concluida') {
      current.servicos_concluidos += 1;
      current.valor_total_gasto += Number(os.valor_total) || 0;
    }

    if (os.data_agendada) {
      if (
        !current.ultimo_servico ||
        new Date(os.data_agendada) > new Date(current.ultimo_servico)
      ) {
        current.ultimo_servico = os.data_agendada;
      }
    }

    if (
      !os.comprovativo_pagamento_url &&
      ['em_execucao', 'concluida'].includes(os.status ?? '')
    ) {
      current.pendencias_pagamento += 1;
    }

    metricsMap.set(os.cliente_id, current);
  }

  return metricsMap;
}

export function combinarClientesComMetricas(
  clientes: ClienteRow[],
  metricsMap: Map<string, MetricasCliente>,
): ClienteReal[] {
  return clientes.map((cliente) => {
    const metrics = metricsMap.get(cliente.id) ?? {
      total_servicos: 0,
      servicos_concluidos: 0,
      valor_total_gasto: 0,
      ultimo_servico: null,
      pendencias_pagamento: 0,
    };

    const temServicos = metrics.total_servicos > 0;
    // Legacy cast-and-dig: if responsavel ever comes back as an array (Supabase quirk for some FK joins),
    // `.nome` on array is undefined → `?? null` lands on null. Preserved as-is from the original hook.
    const responsavelData = cliente.responsavel as { nome: string | null } | null;

    return {
      id: cliente.id,
      codigo_cliente: cliente.codigo_cliente,
      nome: cliente.nome,
      telefone: cliente.telefone,
      email: cliente.email,
      avatar_url: cliente.avatar_url,
      servico_interesse: cliente.servico_interesse,
      endereco_rua: cliente.endereco_rua,
      endereco_numero: cliente.endereco_numero,
      endereco_complemento: cliente.endereco_complemento,
      endereco_cidade: cliente.endereco_cidade,
      endereco_codigo_postal: cliente.endereco_codigo_postal,
      endereco_pais: cliente.endereco_pais,
      responsavel_id: cliente.responsavel_id,
      responsavel_nome: responsavelData?.nome || null,
      arquivado: cliente.arquivado || false,
      origem: cliente.origem,
      criado_em: cliente.criado_em,
      ...metrics,
      status_led: calcularStatusLed(temServicos, metrics.ultimo_servico, metrics.pendencias_pagamento),
      is_lead: !temServicos,
    };
  });
}

export function extrairOrigensUnicas(rows: { origem: string | null }[]): string[] {
  const set = new Set(rows.map((r) => r.origem).filter((o): o is string => Boolean(o)));
  return [...set].sort();
}

export function aplicarFiltrosClientes(
  clientes: ClienteReal[],
  filtros: FiltrosClientesReais | undefined,
  opts: { profileId?: string; isAdmin: boolean },
): ClienteReal[] {
  let resultado = clientes;

  if (filtros?.busca) {
    const busca = filtros.busca.toLowerCase();
    resultado = resultado.filter((c) =>
      c.nome?.toLowerCase().includes(busca) ||
      c.telefone?.includes(busca) ||
      c.email?.toLowerCase().includes(busca) ||
      c.codigo_cliente?.toLowerCase().includes(busca),
    );
  }

  if (filtros?.status && filtros.status.length > 0) {
    resultado = resultado.filter((c) => filtros.status!.includes(c.status_led));
  }

  if (filtros?.tipo && filtros.tipo.length > 0) {
    resultado = resultado.filter((c) => {
      if (filtros.tipo!.includes('lead') && c.is_lead) return true;
      if (filtros.tipo!.includes('cliente') && !c.is_lead) return true;
      return false;
    });
  }

  if (filtros?.origem && filtros.origem.length > 0) {
    resultado = resultado.filter((c) => c.origem !== null && filtros.origem!.includes(c.origem));
  }

  if (filtros?.minServicos !== undefined) {
    resultado = resultado.filter((c) => c.total_servicos >= filtros.minServicos!);
  }

  if (filtros?.maxServicos !== undefined) {
    resultado = resultado.filter((c) => c.total_servicos <= filtros.maxServicos!);
  }

  if (filtros?.minValor !== undefined) {
    resultado = resultado.filter((c) => c.valor_total_gasto >= filtros.minValor!);
  }

  if (filtros?.maxValor !== undefined) {
    resultado = resultado.filter((c) => c.valor_total_gasto <= filtros.maxValor!);
  }

  if (filtros?.ultimoServicoApos) {
    resultado = resultado.filter(
      (c) => c.ultimo_servico !== null && new Date(c.ultimo_servico) >= filtros.ultimoServicoApos!,
    );
  }

  if (filtros?.ultimoServicoAntes) {
    resultado = resultado.filter(
      (c) => c.ultimo_servico !== null && new Date(c.ultimo_servico) <= filtros.ultimoServicoAntes!,
    );
  }

  if (filtros?.primeiroContatoApos) {
    resultado = resultado.filter(
      (c) => c.criado_em !== null && new Date(c.criado_em) >= filtros.primeiroContatoApos!,
    );
  }

  if (filtros?.primeiroContatoAntes) {
    resultado = resultado.filter(
      (c) => c.criado_em !== null && new Date(c.criado_em) <= filtros.primeiroContatoAntes!,
    );
  }

  if (!opts.isAdmin && opts.profileId) {
    resultado = resultado.filter(
      (c) => c.responsavel_id === opts.profileId || !c.responsavel_id,
    );
  }

  return resultado;
}
